/**
 * /about page — plain-language statement of the four rules we hold ourselves
 * to. The content is hand-written prose (not derived from constants) because
 * the point is the promise, not a data table.
 */

export function renderAbout(): HTMLElement {
  const article = document.createElement("article");
  article.innerHTML = `
    <header class="content-header">
      <h1>What we promise</h1>
      <p class="content-lede">
        RentOrOwn.info exists to give you an honest answer to the biggest
        financial question most people ever ask: should I rent, or should I
        buy? Everything else follows from that.
      </p>
    </header>

    <section aria-labelledby="promise">
      <h2 id="promise">The four rules we hold ourselves to</h2>
      <ol>
        <li>
          <strong>No email, no accounts, no tracking.</strong> The calculator
          never asks who you are. There is no signup wall, no analytics pixel,
          no session replay, and no profile of you anywhere. Your scenario
          lives in the URL of your browser tab and nowhere else. The one
          prompt you will see is the advertising-consent banner described
          below — it exists to give you a choice about ads, not to identify
          you.
        </li>
        <li>
          <strong>No affiliates. Advertising, not commissions.</strong> We do
          not take referral fees from mortgage brokers, real-estate portals,
          lenders, or anyone else. The math has no reason to nudge you toward
          buying — or renting — because we are not paid either way. The site is
          supported by contextual advertising served on our
          <a href="/learn/">Learn</a> articles only; the calculator itself is
          ad-free.
        </li>
        <li>
          <strong>Every default is visible and every default is cited.</strong>
          Tap "Source" beside any input to see where the number came from —
          Freddie Mac, the US Census, the Insurance Information Institute,
          etc. Every default is editable. If you do not agree with ours,
          override it and see the answer change live.
        </li>
        <li>
          <strong>Every conclusion is explained in plain English.</strong> The
          results view tells you the break-even year in a single sentence and
          shows the reasoning underneath. If a number cannot be defended in
          plain English, it does not go on the page.
        </li>
      </ol>
    </section>

    <section aria-labelledby="what-we-do-not-collect">
      <h2 id="what-we-do-not-collect">What we do not collect</h2>
      <p>
        Nothing you type into the calculator ever leaves your browser. The
        inputs are serialized into the URL query string so that refreshing,
        bookmarking, and sharing all work — but that URL is only in your
        hands. We do not store scenarios server-side, we do not build user
        profiles, and we do not have a database of your inputs to leak.
      </p>
      <p>
        We store exactly one thing in your browser: your advertising-consent
        choice, under the local-storage key <code>ror.consent.v1</code>, so we
        do not have to ask again on your next visit. We set no cookies of our
        own, and we embed no analytics or fingerprinting script — no Google
        Analytics, no Facebook pixel, no session replay, no heatmaps. The only
        third-party script on the site is Google AdSense, which serves the ads
        on our <a href="/learn/">Learn</a> articles; it stays in a denied
        consent state until you choose otherwise, and it is never given
        anything you typed into the calculator.
      </p>
    </section>

    <section aria-labelledby="what-we-do-collect">
      <h2 id="what-we-do-collect">What we do collect</h2>
      <p>
        To know whether the site is working we count three aggregate
        signals, and only these three:
      </p>
      <ol>
        <li>
          <strong>Page visits</strong> — how many times the calculator
          loaded. This tells us whether anyone is finding the page.
        </li>
        <li>
          <strong>Calculator completions</strong> — how many visits led
          the person to edit at least one input to match their own
          situation. This is our "did we actually help someone answer the
          question" number.
        </li>
        <li>
          <strong>Share-link uses</strong> — how many times the "Copy
          shareable link" button was pressed. This tells us whether the
          share flow is worth its space on the page.
        </li>
      </ol>
      <p>
        Each signal is fired as a same-origin request to a fixed path
        (<code>/beacon/visit</code>, <code>/beacon/complete</code>,
        <code>/beacon/share</code>). The request carries no body, no
        cookie, no user ID, no scenario data — just the URL. Cloudflare,
        our host, counts the requests per path in aggregate. Nobody at
        RentOrOwn.info can look at a single request and learn anything
        about the person who sent it, because there is nothing there to
        look at.
      </p>
      <p>
        We honour the <a
          href="https://globalprivacycontrol.org/"
          target="_blank"
          rel="noopener noreferrer"
        >Global Privacy Control</a> signal and the <code>Do Not Track</code>
        header. If your browser sends either, we send nothing at all.
      </p>
      <p>
        No cookies. No fingerprinting. No cross-session identifiers. No third
        parties in the analytics path at all beyond Cloudflare, which is
        already serving you the HTML you are reading — and no third party
        anywhere on the site except the consent-gated AdSense units on the
        Learn articles. If a future change violates any of these rules, we
        consider it a bug.
      </p>
    </section>

    <section aria-labelledby="who">
      <h2 id="who">Who is behind this</h2>
      <p>
        A small independent team who got tired of every rent-vs-buy calculator
        on the web being either a lead-generation funnel for brokers or a
        black box built on undisclosed assumptions. We open the assumptions,
        drop the funnel, and let the math speak.
      </p>
      <p>
        Full details of every formula, default, and source live on the
        <a href="/methodology">methodology page</a>. If you find something you
        disagree with, tell us — the calculator improves fastest when people
        push back on our numbers.
      </p>
    </section>

    <section aria-labelledby="start-here">
      <h2 id="start-here">Where to start</h2>
      <p>
        If you came here to check whether we are worth trusting before you
        trust a number, these are the three doors into the site:
      </p>
      <ul class="related-links">
        <li>
          <a href="/">The rent-vs-buy calculator</a> — enter your rent, a home
          price, and your horizon, and get a break-even year in under two
          minutes. Nothing to sign up for, nothing to dismiss.
        </li>
        <li>
          <a href="/methodology">The methodology page</a> — every default, every
          formula, and every citation, generated from the same constants the
          calculator runs on, so it cannot drift out of date.
        </li>
        <li>
          <a href="/learn/">The Learn library</a> — the long-form arguments
          behind the defaults. Good places to begin:
          <a href="/learn/how-to-actually-compare-renting-vs-buying/">the five numbers most calculators skip</a>,
          <a href="/learn/break-even-year-plain-english/">break-even year in plain English</a>,
          and
          <a href="/learn/opportunity-cost-invest-down-payment/">what happens if you invest your down payment instead</a>.
        </li>
      </ul>
      <p>
        Our <a href="/privacy">privacy policy</a> and
        <a href="/terms">terms</a> spell out the same promises in the language
        a lawyer would use. If the two ever disagree with this page, this page
        is the one we intend to be held to.
      </p>
    </section>
  `;
  return article;
}
